/**
 * touchPopover — tap-to-toggle behavior for hover-only popovers, so
 * touch users can reach the master-volume slider, the metronome
 * volume, and similar reveal-on-hover affordances.
 *
 * Usage:
 *
 *   attachTouchPopover({
 *     wrap: document.querySelector('.topbar-volume-wrap'),
 *     trigger: document.querySelector('.topbar-volume-button'),
 *   });
 *
 * The CSS rule for the popover should already include `.is-open` as
 * an "open" state alongside `:hover` / `:focus-within`. This helper
 * just toggles that class on tap-equivalent events and closes it on
 * outside taps. Hover behavior continues to work unchanged on devices
 * that support it.
 *
 * We don't gate on `(hover: hover)` — even on a hover-capable device,
 * an extra click toggle is harmless (the popover is already showing
 * via :hover) and the resulting `.is-open` clears once the user
 * moves elsewhere. Simpler than branching by capability.
 */

export interface TouchPopoverOptions {
  /** The wrap element that gets the `.is-open` class. */
  readonly wrap: HTMLElement;
  /**
   * Element that opens the popover when clicked. Defaults to the
   * wrap. Pass a more specific element (e.g. just the button)
   * when other interactive children inside the wrap shouldn't
   * trigger the toggle.
   */
  readonly trigger?: HTMLElement;
  /**
   * When true, clicks on the trigger toggle open/closed; when false
   * (default), clicks always open and an outside click is required
   * to close. Useful when the trigger has its own primary action
   * (like the metronome button) — set false there.
   */
  readonly toggle?: boolean;
}

/**
 * Wires tap-to-open and outside-tap-to-close behavior. Returns a
 * cleanup function so callers in long-lived code can detach.
 */
export function attachTouchPopover(opts: TouchPopoverOptions): () => void {
  const wrap = opts.wrap;
  const trigger = opts.trigger ?? wrap;
  const toggle = opts.toggle ?? false;

  const onTriggerClick = (e: MouseEvent): void => {
    // Don't capture clicks on form inputs inside the wrap (the slider
    // itself, etc.) — those should drive their own interactions, not
    // close the popover.
    if (e.target instanceof HTMLInputElement) return;
    if (toggle) {
      wrap.classList.toggle('is-open');
    } else {
      wrap.classList.add('is-open');
    }
  };

  const onDocClick = (e: MouseEvent): void => {
    if (!wrap.classList.contains('is-open')) return;
    if (e.target instanceof Node && wrap.contains(e.target)) return;
    wrap.classList.remove('is-open');
  };

  // Escape closes — common popover muscle memory.
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return;
    if (!wrap.classList.contains('is-open')) return;
    wrap.classList.remove('is-open');
  };

  trigger.addEventListener('click', onTriggerClick);
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onKeyDown);

  return () => {
    trigger.removeEventListener('click', onTriggerClick);
    document.removeEventListener('click', onDocClick);
    document.removeEventListener('keydown', onKeyDown);
  };
}
